import type { ReactNode } from 'react';
import { S, R, T } from '../theme';

/**
 * The one status chip.
 *
 * Premise had grown four of these (Pill, the Collections table's inline `pill`,
 * the Rooms board's inline chip, and SampleBadge) with four paddings, two font
 * sizes, two weights and two radii — one of which was 99 rather than 999, a typo
 * that renders identically and so was never caught. This replaces all four.
 *
 * Tone carries meaning, size carries density: `md` in tables and rosters, `sm`
 * on cards where the chip sits under other text.
 */
export type ChipTone = keyof typeof S.status;
export type ChipSize = 'sm' | 'md';

const SIZES = {
  sm: { fontSize: T.label, padding: '2px 8px', fontWeight: 700 as const, gap: 5 },
  md: { fontSize: 12, padding: '3px 10px', fontWeight: 600 as const, gap: 6 },
};

export function Chip({
  tone = 'neutral',
  size = 'md',
  dot = false,
  caps = false,
  outline = false,
  children,
}: {
  tone?: ChipTone;
  size?: ChipSize;
  /** Leading filled dot, used by the ledger status pills. */
  dot?: boolean;
  /** Uppercase micro-label, used by the sample-data badge. */
  caps?: boolean;
  /** Hairline border, so a pale chip still reads on a white card. */
  outline?: boolean;
  children: ReactNode;
}) {
  const { fg, bg } = S.status[tone];
  const s = SIZES[size];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: s.gap,
        fontSize: s.fontSize,
        fontWeight: s.fontWeight,
        padding: s.padding,
        borderRadius: R.pill,
        background: bg,
        color: fg,
        border: outline ? `1px solid ${S.edge.hairline}` : undefined,
        letterSpacing: caps ? '.04em' : undefined,
        textTransform: caps ? 'uppercase' : undefined,
        whiteSpace: 'nowrap',
        verticalAlign: 'middle',
      }}
    >
      {dot && (
        <span style={{ width: 6, height: 6, borderRadius: R.pill, background: 'currentColor', flex: 'none' }} />
      )}
      {children}
    </span>
  );
}
